import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Calendar, MapPin, Users, Plus, Filter } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { eventService } from '@/lib/api/event.service';
import { celebrationService } from '@/lib/api/celebration.service';
import type { Event, EventStatus } from '@/types/event.types';
import type { CelebrationWithEvents, EventForCelebration } from '@/types/celebration.types';
import { formatEventDateRange } from '@/utils/dateFormat';

export const EventList: React.FC = () => {
  const { t } = useTranslation(['celebrations', 'events', 'common']);
  const { id: celebrationId } = useParams();
  const [events, setEvents] = useState<(Event | EventForCelebration)[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<(Event | EventForCelebration)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<EventStatus | 'all'>('active');
  const [celebration, setCelebration] = useState<CelebrationWithEvents | null>(null);

  // Determine if we're viewing celebration-specific events
  const isCelebrationSpecific = Boolean(celebrationId);

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, statusFilter]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      if (isCelebrationSpecific && celebrationId) {
        // Load celebration with its events
        const celebrationData = await celebrationService.getCelebrationWithEvents(parseInt(celebrationId));
        setCelebration(celebrationData);
        setEvents(celebrationData.events);
      } else {
        // Load all user events
        const data = await eventService.getEvents();
        setEvents(data);
      }
      setError(null);
    } catch (err) {
      setError(isCelebrationSpecific ? 'Failed to load celebration events' : 'Failed to load events');
      console.error('Error loading events:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    let filtered = events;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(event => event.status === statusFilter);
    }

    setFilteredEvents(filtered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper functions to safely access event properties
  const hasMaxGuests = (event: Event | EventForCelebration): event is Event => {
    return 'max_guests' in event;
  };

  const hasCreatedAt = (event: Event | EventForCelebration): event is Event => {
    return 'created_at' in event;
  };

  const handleDelete = async (eventId: number) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await eventService.deleteEvent(eventId);
      // Refresh event list after successful deletion
      await loadEvents();
    } catch (error: any) {
      console.error('Error deleting event:', error);
      
      // Check for specific auth token issues
      const isAuthError = error.response?.status === 401;
      const isBearerTokenError = error.response?.data?.message?.includes('Bearer token is required');
      
      if (isAuthError || isBearerTokenError) {
        // Clear corrupted auth state and redirect to login
        console.warn('Auth token issue detected during delete, clearing storage and redirecting');
        // Handle auth error if needed
        alert('Your session has expired. Please refresh the page and log in again.');
      } else {
        // Provide more detailed error message
        let errorMessage = 'Failed to delete event. Please try again.';
        if (error.response?.data?.message) {
          errorMessage = `Failed to delete event: ${error.response.data.message}`;
        } else if (error.message) {
          errorMessage = `Failed to delete event: ${error.message}`;
        }
        alert(errorMessage);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs />
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">
            {isCelebrationSpecific ? `Events for ${celebration?.title || 'Celebration'}` : t('celebrations:title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isCelebrationSpecific 
              ? `Manage events for this ${celebration?.celebration_type || 'celebration'}`
              : 'Manage your celebrations and special events'
            }
          </p>
        </div>
        <Link to={isCelebrationSpecific ? `/celebrations/${celebrationId}/events/new` : "/events/new"}>
          <Button className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {isCelebrationSpecific ? 'Create Event' : 'Create Celebration'}
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex justify-end">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as EventStatus | 'all')}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="draft">{t('common:status.draft')}</option>
            <option value="active">{t('common:status.active')}</option>
            <option value="completed">{t('common:status.completed')}</option>
            <option value="cancelled">{t('common:status.cancelled')}</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {filteredEvents.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {isCelebrationSpecific ? 'No events found' : 'No celebrations found'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {events.length === 0
              ? (isCelebrationSpecific
                  ? 'Get started by creating your first event for this celebration.'
                  : 'Get started by creating your first celebration.')
              : 'Try adjusting your filters.'}
          </p>
          {events.length === 0 && (
            <div className="mt-6">
              <Link to={isCelebrationSpecific ? `/celebrations/${celebrationId}/events/new` : "/events/new"}>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  {isCelebrationSpecific ? 'Create Event' : 'Create Celebration'}
                </Button>
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <Card key={event.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{event.title}</CardTitle>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                    {event.status}
                  </span>
                </div>
                {/* Only show description if present */}
                {event.description && (
                  <CardDescription className="line-clamp-2">
                    {event.description}
                  </CardDescription>
                )}
                {/* Show event time range below title */}
                <div className="flex items-center text-sm text-gray-500 mt-2">
                  <Calendar className="h-4 w-4 mr-2" />
                  {event.start_time && event.end_time 
                    ? formatEventDateRange(event.start_time, event.end_time)
                    : formatDate(event.event_date)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(event.venue_name || event.venue_location) && (
                    <div className="flex items-center text-sm text-gray-500">
                      <MapPin className="h-4 w-4 mr-2" />
                      {event.venue_name && event.venue_location 
                        ? `${event.venue_name}, ${event.venue_location}`
                        : event.venue_name || event.venue_location}
                    </div>
                  )}
                  {hasMaxGuests(event) && event.max_guests && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-2" />
                      Max {event.max_guests} guests
                    </div>
                  )}
                </div>
                <div className="mt-4 flex justify-between items-center">
                  {hasCreatedAt(event) ? (
                    <span className="text-xs text-gray-400">
                      Created {new Date(event.created_at).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">
                      {/* No creation date available for celebration events */}
                    </span>
                  )}
                  <div className="flex gap-2">
                    <Link to={`/events/${event.id}`}>
                      <Button variant="outline" size="sm" data-testid={`event-view-details-${event.id}`}>
                        View Details
                      </Button>
                    </Link>
                    <button
                      onClick={() => handleDelete(event.id)}
                      className="text-red-600 hover:underline"
                      data-testid={`delete-event-${event.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PageContainer>
  );
};
